import types from '../../mutation-types';
import getters, { getSelectedChatConversation, getConversationById } from './getters';
import actions from './actions';
import {
  findPendingMessageIndex,
  filterByStatus,
  filterByInbox,
  filterByTeam,
  filterByLabel,
} from './helpers';
import { MESSAGE_STATUS } from 'shared/constants/messages';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import wootConstants from 'dashboard/constants/globals';

export const state = {
  allConversations: [],
  selectedChatId: null,
  activeChat: null,
  listLoadingStatus: true,
  chatStatusFilter: wootConstants.STATUS_TYPE.OPEN,
  chatSortFilter: wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC,
  currentInbox: null,
  conversationFilters: {
    page: 1,
  },
  appliedFilters: [],
  conversationParticipants: [],
  conversationLastSeen: [],
  attachments: {},
  contextMenuChatId: null,
  syncConversationsMessages: {},
  copilotAssistant: null,
};

export const mutations = {
  [types.SET_ALL_CONVERSATION](_state, conversationList) {
    const newAllConversations = [..._state.allConversations];
    conversationList.forEach(conversation => {
      const indexInCurrentList = newAllConversations.findIndex(
        c => Number(c.id) === Number(conversation.id)
      );
      if (indexInCurrentList < 0) {
        newAllConversations.push(conversation);
      } else if (Number(conversation.id) !== Number(_state.selectedChatId)) {
        newAllConversations[indexInCurrentList] = conversation;
      } else {
        const existingConversation = newAllConversations[indexInCurrentList];
        newAllConversations[indexInCurrentList] = {
          ...conversation,
          allMessagesLoaded: existingConversation.allMessagesLoaded,
          messages: existingConversation.messages || conversation.messages,
          dataFetched: existingConversation.dataFetched,
        };
      }
    });
    _state.allConversations = newAllConversations;
  },

  [types.EMPTY_ALL_CONVERSATION](_state) {
    const currentChat =
      _state.activeChat ||
      _state.allConversations.find(
        c =>
          Number(c.id) === Number(_state.selectedChatId) ||
          Number(c.display_id) === Number(_state.selectedChatId)
      );
    _state.allConversations = currentChat ? [currentChat] : [];
  },

  [types.CLEAR_ALL_MESSAGES_LOADED](_state, chatID) {
    const [chat] = _state.allConversations.filter(c => c.id === chatID);
    if (chat) {
      chat.allMessagesLoaded = false;
    }
  },

  [types.SET_ALL_MESSAGES_LOADED](_state, chatID) {
    const [chat] = _state.allConversations.filter(c => c.id === chatID);
    if (chat) {
      chat.allMessagesLoaded = true;
    }
  },

  [types.SET_CURRENT_CHAT_WINDOW](_state, activeChat) {
    if (activeChat) {
      _state.selectedChatId = activeChat.id;
      _state.activeChat = activeChat;
      const exists = _state.allConversations.some(
        c => Number(c.id) === Number(activeChat.id) || Number(c.display_id) === Number(activeChat.display_id)
      );
      if (!exists) {
        _state.allConversations.unshift(activeChat);
      }
    }
  },

  [types.CLEAR_CURRENT_CHAT_WINDOW](_state) {
    _state.selectedChatId = null;
    _state.activeChat = null;
  },

  [types.SET_CONVERSATION_CAN_REPLY](_state, { conversationId, canReply }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (chat) {
      chat.can_reply = canReply;
    }
  },

  [types.SET_CHAT_DATA_FETCHED](_state, chatID) {
    const [chat] = _state.allConversations.filter(c => c.id === chatID);
    if (chat) {
      chat.dataFetched = true;
    }
  },

  [types.SET_ALL_ATTACHMENTS](_state, { id, data }) {
    _state.attachments[id] = data;
  },

  [types.SET_PREVIOUS_CONVERSATIONS](_state, { id, data }) {
    if (!data || !data.length) return;
    const [chat] = _state.allConversations.filter(c => c.id === id);
    if (!chat) return;

    if (!chat.messages) {
      chat.messages = [];
    }
    const existingIds = new Set(chat.messages.map(m => m.id));
    const newMessages = data.filter(m => !existingIds.has(m.id));
    if (newMessages.length) {
      chat.messages.unshift(...newMessages);
    }
  },

  [types.SET_MISSING_MESSAGES](_state, { id, data }) {
    const [chat] = _state.allConversations.filter(c => c.id === id);
    if (!chat) return;

    chat.messages = data;
  },

  [types.CHANGE_CHAT_STATUS_FILTER](_state, data) {
    _state.chatStatusFilter = data;
  },

  [types.CHANGE_CHAT_SORT_FILTER](_state, data) {
    _state.chatSortFilter = data;
  },

  [types.UPDATE_MESSAGE_UNREAD_COUNT](_state, { id, lastSeen, unreadCount = 0 }) {
    const [chat] = _state.allConversations.filter(c => c.id === id);
    if (chat) {
      chat.agent_last_seen_at = lastSeen;
      chat.unread_count = unreadCount;
    }
  },

  [types.ADD_MESSAGE]({ allConversations, selectedChatId }, message) {
    const { conversation_id: conversationId } = message;
    const index = allConversations.findIndex(c => c.id === conversationId || c.display_id === conversationId);
    if (index === -1) return;

    const chat = allConversations[index];
    if (!chat.messages) {
      chat.messages = [];
    }
    const pendingMessageIndex = findPendingMessageIndex(chat, message);
    const existingMessageIndex = chat.messages.findIndex(
      m => m.id && message.id && m.id === message.id
    );
    const msgTime =
      typeof message.created_at === 'number'
        ? message.created_at
        : Math.floor(new Date(message.created_at).getTime() / 1000);

    if (pendingMessageIndex !== -1) {
      chat.messages[pendingMessageIndex] = message;
    } else if (existingMessageIndex !== -1) {
      chat.messages[existingMessageIndex] = message;
    } else {
      chat.messages.push(message);
      chat.timestamp = msgTime;
      chat.last_activity_at = msgTime;
      const { conversation: { unread_count: unreadCount = 0 } = {} } = message;
      chat.unread_count = unreadCount;

      if (index > 0) {
        allConversations.splice(index, 1);
        allConversations.unshift(chat);
      }

      if (selectedChatId === conversationId) {
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
      }
    }
  },

  [types.ADD_CONVERSATION](_state, conversation) {
    const exists = _state.allConversations.some(c => c.id === conversation.id);
    if (!exists) {
      _state.allConversations.unshift(conversation);
    }
  },

  [types.DELETE_CONVERSATION](_state, conversationId) {
    _state.allConversations = _state.allConversations.filter(
      c => c.id !== conversationId
    );
  },

  [types.UPDATE_CONVERSATION](_state, conversation) {
    const { allConversations } = _state;
    const index = allConversations.findIndex(
      c =>
        Number(c.id) === Number(conversation.id) ||
        Number(c.display_id) === Number(conversation.display_id)
    );

    if (index > -1) {
      const selectedConversation = allConversations[index];
      if (conversation.updated_at < selectedConversation.updated_at) {
        return;
      }
      const { messages, ...updates } = conversation;
      allConversations[index] = { ...selectedConversation, ...updates };
    } else {
      const { conversationType } = _state.conversationFilters || {};
      const { MENTION, PARTICIPATING } = wootConstants.CONVERSATION_TYPE;
      if (![MENTION, PARTICIPATING].includes(conversationType)) {
        allConversations.unshift(conversation);
      }
    }
  },

  [types.SET_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = true;
  },

  [types.CLEAR_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = false;
  },

  [types.UPDATE_ASSIGNEE](_state, payload) {
    const chat = getConversationById(_state)(payload.id);
    if (chat) {
      chat.meta.assignee = payload.assignee;
    }
  },

  [types.UPDATE_CONVERSATION_CONTACT](_state, { conversationId, ...payload }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (chat) {
      chat.meta.sender = payload;
    }
  },

  [types.UPDATE_MESSAGE_CALL_STATUS](_state, { conversationId, callStatus, callSid }) {
    const chat = getConversationById(_state)(conversationId);
    if (!chat) return;

    const message = (chat.messages || []).find(
      m =>
        m.content_type === 'voice_call' &&
        m.call?.provider_call_id === callSid
    );
    if (!message?.call) return;

    message.call = { ...message.call, status: callStatus };
  },

  [types.SET_ACTIVE_INBOX](_state, inboxId) {
    _state.currentInbox = inboxId ? parseInt(inboxId, 10) : null;
  },

  [types.CLEAR_CONTACT_CONVERSATIONS](_state, contactId) {
    const chats = _state.allConversations.filter(
      c => c.meta.sender.id !== contactId
    );
    _state.allConversations = chats;
  },

  [types.SET_CONVERSATION_FILTERS](_state, data) {
    _state.appliedFilters = data;
  },

  [types.CLEAR_CONVERSATION_FILTERS](_state) {
    _state.appliedFilters = [];
  },

  [types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION](_state, { conversationId, messageId }) {
    _state.syncConversationsMessages[conversationId] = messageId;
  },

  [types.SET_CONTEXT_MENU_CHAT_ID](_state, chatId) {
    _state.contextMenuChatId = chatId;
  },

  [types.SET_CHAT_LIST_FILTERS](_state, data) {
    _state.conversationFilters = data;
  },

  [types.UPDATE_CHAT_LIST_FILTERS](_state, data) {
    _state.conversationFilters = { ..._state.conversationFilters, ...data };
  },

  [types.SET_INBOX_CAPTAIN_ASSISTANT](_state, data) {
    _state.copilotAssistant = data.assistant;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
